import * as ActionGenerics from "../../Common/actionGenerics";
import * as ActionTypes from "./actionTypes";
import * as StoreTypes from "../DataTypes/storeTypes";
import * as AvailabilityActions from "./availabilityActions";
import * as ProviderActions from "./providerActions";
import * as LocationActions from "./locationActions";
import * as ServiceActions from "./serviceActions";
import * as ClientActions from "./clientActions";
import * as StoreManager from "../storeManager";
import * as OrganisationInfo from "../../Common/organisationInfo";
import * as ConfirmActions from "./confirmationActions";
import { selectPortalPaymentMode } from "../Helpers/selectors";

function getData(dispatch, state: StoreTypes.IStore, getState: () => StoreTypes.IStore) {
    const currentStep = state.common.currentStep;

    dispatch(setPageLoading(true));

    let promise: Promise<any> = Promise.resolve();

    switch (currentStep) {
        case StoreTypes.Step.Location:
            promise = LocationActions.getLocations(dispatch, state);
            break;
        case StoreTypes.Step.Service:
            promise = ServiceActions.getServices(dispatch, state);
            break;
        case StoreTypes.Step.Provider:
            promise = ProviderActions.getProviders(dispatch, state);
            break;

        case StoreTypes.Step.When:
            promise = AvailabilityActions.getAvailability(dispatch, state);
            break;

        case StoreTypes.Step.Confirmation: {
            const confirmationInfo = state.stepsInfo[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;
            const portalPaymentSetting = selectPortalPaymentMode(state);
            const hasPayment = portalPaymentSetting > OrganisationInfo.ClientPortalPaymentModes.Disabled;

            promise = ClientActions.getClients(dispatch, state);

            if (!state.servicesData.services || state.servicesData.services.length === 0) {
                if (state.initialSettings.type == StoreTypes.BookingType.Class) {
                    promise = promise.then(() => ServiceActions.getAllServices(dispatch, state));
                } else {
                    promise = promise.then(() => ServiceActions.getServices(dispatch, state));
                }
            }

            promise.then((action) => {
                const latestState = getState();
                if (!Array.isArray(latestState.common.clients) || latestState.common.clients.length === 0) {
                    // GetClients() returned no clients. Booking will not work in this case.
                    dispatch(resetBooking());
                }
                const clientId = confirmationInfo.clientId; //client id must be available at this moment
                dispatch(ServiceActions.applyClientDiscount(clientId));
                dispatch(ServiceActions.tryToApplyDeposit());
                dispatch(ServiceActions.applyTax());
                dispatch(ServiceActions.calculateStripeSurcharges());
            });

            if (hasPayment) {
                promise = promise.then(() => ConfirmActions.getCreditCard(dispatch, state));
            }
            break;
        }
    }

    if (
        (currentStep === StoreTypes.Step.When || currentStep === StoreTypes.Step.Confirmation) &&
        (!state.providers || state.providers.length === 0)
    ) {
        promise = promise.then(() => ProviderActions.getProviders(dispatch, state));
    }

    promise.then(() => dispatch(setPageLoading(false)));
}

function moveNextInternal(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.MoveNext };
}

export function moveNext() {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        dispatch(moveNextInternal());

        const state = getState();

        getData(dispatch, state, getState);
    };
}

function moveBackInternal(): ActionGenerics.IAction<ActionTypes.Actions> {
    return { type: ActionTypes.Actions.MoveBack };
}

export function moveBack() {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        dispatch(moveBackInternal());

        const state = getState();

        getData(dispatch, state, getState);
    };
}

function setStepInternal(step: StoreTypes.Step): ActionGenerics.IParamAction<ActionTypes.Actions, StoreTypes.Step> {
    return { type: ActionTypes.Actions.SetStep, parameter: step };
}

export function setStep(step: StoreTypes.Step) {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        dispatch(setStepInternal(step));

        const state = getState();

        getData(dispatch, state, getState);
    };
}

export function requestCancel(isRequested: boolean): ActionGenerics.IParamAction<ActionTypes.Actions, boolean> {
    return { type: ActionTypes.Actions.RequestCancel, parameter: isRequested };
}

export function cancel() {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        const state = getState();

        StoreManager.flush(state.initialSettings.accountInitialSettings.organisationInfo.sgId);

        window.location.assign(state.initialSettings.cancelUrl);
    };
}

export function setPageLoading(isLoading: boolean): ActionGenerics.IParamAction<ActionTypes.Actions, boolean> {
    return { type: ActionTypes.Actions.SetPageLoading, parameter: isLoading };
}

export function updateIsBusy(isBusy: boolean): ActionGenerics.IParamAction<ActionTypes.Actions, boolean> {
    return { type: ActionTypes.Actions.UpdateIsBusy, parameter: isBusy };
}

export function flushSteps(
    steps: StoreTypes.Step[]
): ActionGenerics.IParamAction<ActionTypes.Actions, StoreTypes.Step[]> {
    return { type: ActionTypes.Actions.FlushSteps, parameter: steps };
}

export function resetBooking() {
    return (dispatch, getState: () => StoreTypes.IStore) => {
        const state = getState();
        StoreManager.flush(state.initialSettings.accountInitialSettings.organisationInfo.sgId);

        dispatch(
            flushSteps([
                StoreTypes.Step.Location,
                StoreTypes.Step.Service,
                StoreTypes.Step.Provider,
                StoreTypes.Step.When,
                StoreTypes.Step.Confirmation,
            ])
        );

        dispatch(setStep(StoreTypes.Step.Location));
    };
}
